import { Info, Sparkles } from 'lucide-react'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { MAX_PURPOSE_CUSTOM_LENGTH } from '@/constants/artifacts'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

import type { FileLabel, FileLabelSuggestion } from './analysis-modal/types'

type TooltipSide = 'left' | 'right' | 'bottom'

function measurePurposeTooltipSides(container: HTMLElement): Record<string, 'left' | 'right'> {
	const containerRect = container.getBoundingClientRect()
	if (containerRect.width === 0) return {}

	const midX = containerRect.left + containerRect.width / 2
	const next: Record<string, 'left' | 'right'> = {}

	container.querySelectorAll<HTMLElement>('[data-purpose-option]').forEach((el) => {
		const key = el.dataset.purposeOption
		if (!key) return
		const rect = el.getBoundingClientRect()
		const centerX = rect.left + rect.width / 2
		// Outward from the chip cluster: left half opens left, right half opens right.
		next[key] = centerX < midX ? 'left' : 'right'
	})

	return next
}

const TIME_DIMENSION_OPTIONS = ['as_is', 'to_be', 'other'] as const

const PURPOSE_OPTIONS_PRIMARY = [
	'process_description',
	'process_interview',
	'process_mining_report',
	'system_technical',
	'reference_framework',
	'norm_standard',
	'other_custom',
] as const

const PURPOSE_OPTIONS_CONTEXT = [
	'process_description',
	'process_interview',
	'process_mining_report',
	'system_technical',
	'reference_framework',
	'norm_standard',
	'background_source',
	'other_custom',
] as const

export interface FileLabelSelectorProps {
	/** Current label values */
	label: FileLabel
	/** Callback when label changes */
	onChange: (label: FileLabel) => void
	/** Whether this is for a context file (shows additional purpose options) */
	isContextFile?: boolean
	/** Use plain styling (no gray box, white background) */
	variant?: 'default' | 'plain'
	/** Optional className for the container */
	className?: string
	/** Disable all interactions (read-only mode) */
	disabled?: boolean
	/** Disable helper tooltips (used in guided tour mode) */
	disableTooltips?: boolean
	/** Classifier suggestion; renders a "suggested" badge with a rationale tooltip */
	suggestion?: FileLabelSuggestion
}

export function FileLabelSelector({
	label,
	onChange,
	isContextFile = false,
	variant = 'default',
	className,
	disabled = false,
	disableTooltips = false,
	suggestion,
}: FileLabelSelectorProps) {
	const { t } = useTranslation()
	const purposeOptions = isContextFile ? PURPOSE_OPTIONS_CONTEXT : PURPOSE_OPTIONS_PRIMARY
	const purposeOptionsRef = useRef<HTMLDivElement>(null)
	const [purposeTooltipSides, setPurposeTooltipSides] = useState<Record<string, 'left' | 'right'>>(
		{}
	)

	useLayoutEffect(() => {
		const container = purposeOptionsRef.current
		if (!container || disableTooltips) return

		const syncSides = () => {
			const next = measurePurposeTooltipSides(container)
			setPurposeTooltipSides((prev) => {
				const keys = Object.keys(next)
				if (keys.length === Object.keys(prev).length && keys.every((key) => prev[key] === next[key])) {
					return prev
				}
				return next
			})
		}

		syncSides()
		const resizeObserver = new ResizeObserver(syncSides)
		resizeObserver.observe(container)
		const mutationObserver = new MutationObserver(syncSides)
		mutationObserver.observe(container, { childList: true, subtree: true })
		return () => {
			resizeObserver.disconnect()
			mutationObserver.disconnect()
		}
	}, [disableTooltips])

	const timeDimensionLabels: Record<string, string> = {
		as_is: t('pages.projectDetail.modal.labels.asIs') as string,
		to_be: t('pages.projectDetail.modal.labels.toBe') as string,
		other: t('pages.projectDetail.modal.labels.other') as string,
	}

	const timeDimensionTooltips: Record<string, string> = {
		as_is: t('pages.projectDetail.modal.labels.asIsTooltip') as string,
		to_be: t('pages.projectDetail.modal.labels.toBeTooltip') as string,
		other: t('pages.projectDetail.modal.labels.otherTooltip') as string,
	}

	const purposeLabels: Record<string, string> = {
		process_description: t('pages.projectDetail.modal.labels.processDescription') as string,
		process_interview: t('pages.projectDetail.modal.labels.processInterview') as string,
		process_mining_report: t('pages.projectDetail.modal.labels.processMiningReport') as string,
		system_technical: t('pages.projectDetail.modal.labels.systemTechnical') as string,
		reference_framework: t('pages.projectDetail.modal.labels.referenceFramework') as string,
		norm_standard: t('pages.projectDetail.modal.labels.normStandard') as string,
		background_source: t('pages.projectDetail.modal.labels.backgroundSource') as string,
		// Display-only for legacy rows; not in PURPOSE_OPTIONS_* selectable chips.
		normative_standard: t('pages.projectDetail.modal.labels.normativeStandard') as string,
		// Display-only for legacy rows; not in PURPOSE_OPTIONS_* selectable chips.
		additional_source: t('pages.projectDetail.modal.labels.additionalSource') as string,
		other_custom: t('pages.projectDetail.modal.labels.otherCustom') as string,
	}

	const purposeTooltips: Record<string, string> = {
		process_description: t('pages.projectDetail.modal.labels.processDescriptionTooltip') as string,
		process_interview: t('pages.projectDetail.modal.labels.processInterviewTooltip') as string,
		process_mining_report: t('pages.projectDetail.modal.labels.processMiningReportTooltip') as string,
		system_technical: t('pages.projectDetail.modal.labels.systemTechnicalTooltip') as string,
		reference_framework: t('pages.projectDetail.modal.labels.referenceFrameworkTooltip') as string,
		norm_standard: t('pages.projectDetail.modal.labels.normStandardTooltip') as string,
		background_source: t('pages.projectDetail.modal.labels.backgroundSourceTooltip') as string,
		normative_standard: t('pages.projectDetail.modal.labels.normativeStandardTooltip') as string,
		other_custom: t('pages.projectDetail.modal.labels.otherCustomTooltip') as string,
	}

	const withTooltip = (
		content: ReactNode,
		tooltip: string,
		side: TooltipSide = 'bottom',
		/** Keep preferred side so chips don't flip over neighbouring options. */
		avoidCollisions = true
	) => {
		if (disableTooltips) return content
		return (
			<Tooltip>
				<TooltipTrigger asChild>{content}</TooltipTrigger>
				<TooltipContent side={side} avoidCollisions={avoidCollisions} className="max-w-[240px]">
					{tooltip}
				</TooltipContent>
			</Tooltip>
		)
	}

	const suggestionRationaleText = suggestion?.rationale
		? (t('pages.projectDetail.modal.labels.suggestionRationale', {
				rationale: suggestion.rationale,
			}) as string)
		: (t('pages.projectDetail.modal.labels.suggestedByAi') as string)

	const suggestionBadge = (axis: 'timeDimension' | 'purpose') => {
		const suggestedValue = axis === 'timeDimension' ? suggestion?.timeDimension : suggestion?.purpose
		// Show whenever a pending suggestion exists — including when the current
		// selection already matches it (upload modal pre-selects the suggestion).
		if (!suggestedValue) return null

		const badge = (
			<button
				type="button"
				className="inline-flex cursor-help border-0 bg-transparent p-0"
				aria-label={suggestionRationaleText}
				title={disableTooltips ? suggestionRationaleText : undefined}
			>
				<Badge variant="secondary" className="pointer-events-none text-[10px] font-normal">
					<Sparkles className="mr-1 size-3" aria-hidden />
					{t('pages.projectDetail.modal.labels.suggested') as string}
				</Badge>
			</button>
		)

		return withTooltip(badge, suggestionRationaleText, 'left')
	}

	const isSuggestedOption = (axis: 'timeDimension' | 'purpose', opt: string) => {
		const suggestedValue = axis === 'timeDimension' ? suggestion?.timeDimension : suggestion?.purpose
		return Boolean(suggestedValue && suggestedValue === opt)
	}

	return (
		<div
			className={cn(
				'space-y-3',
				variant === 'default' && 'p-3 rounded-lg bg-muted/40 border border-border',
				className
			)}
		>
			<div className="space-y-2">
				<div className="flex items-center gap-1.5">
					<Label className="text-xs font-semibold text-foreground uppercase tracking-wide">
						{t('pages.projectDetail.modal.labels.timeDimension') as string}{' '}
						<span className="font-normal normal-case">
							{t('pages.projectDetail.modal.labels.timeDimensionRequired') as string}
						</span>
					</Label>
					{!disableTooltips && (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									className="inline-flex text-muted-foreground hover:text-foreground cursor-help p-0 bg-transparent border-0"
									aria-label={t('pages.projectDetail.modal.labels.timeDimensionTooltip') as string}
								>
									<Info className="size-3.5" aria-hidden />
								</button>
							</TooltipTrigger>
							<TooltipContent side="left" className="max-w-[240px]">
								{t('pages.projectDetail.modal.labels.timeDimensionTooltip') as string}
							</TooltipContent>
						</Tooltip>
					)}
					{suggestionBadge('timeDimension')}
				</div>
				<div className="flex flex-wrap gap-1.5">
					{TIME_DIMENSION_OPTIONS.map((opt) => (
						<div key={opt}>
							{withTooltip(
								<Button
									type="button"
									variant={label.timeDimension === opt ? 'default' : 'outline'}
									size="sm"
									className={cn(
										label.timeDimension === opt &&
											'ring-2 ring-primary ring-offset-2 ring-offset-background font-semibold'
									)}
									onClick={() => {
										if (label.timeDimension === opt) return
										onChange({ ...label, timeDimension: opt })
									}}
									disabled={disabled}
									title={isSuggestedOption('timeDimension', opt) ? suggestionRationaleText : undefined}
								>
									{isSuggestedOption('timeDimension', opt) && (
										<Sparkles className="mr-1 size-3 shrink-0" aria-hidden />
									)}
									{timeDimensionLabels[opt]}
								</Button>,
								timeDimensionTooltips[opt],
								'bottom'
							)}
						</div>
					))}
				</div>
			</div>

			<div className="space-y-2">
				<div className="flex items-center gap-1.5">
					<Label className="text-xs font-semibold text-foreground uppercase tracking-wide">
						{t('pages.projectDetail.modal.labels.purpose') as string}{' '}
						<span className="font-normal normal-case">
							{t('pages.projectDetail.modal.labels.purposeOptional') as string}
						</span>
					</Label>
					{!disableTooltips && (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									className="inline-flex text-muted-foreground hover:text-foreground cursor-help p-0 bg-transparent border-0"
									aria-label={t('pages.projectDetail.modal.labels.purposeTooltip') as string}
								>
									<Info className="size-3.5" aria-hidden />
								</button>
							</TooltipTrigger>
							<TooltipContent side="left" className="max-w-[240px]">
								{t('pages.projectDetail.modal.labels.purposeTooltip') as string}
							</TooltipContent>
						</Tooltip>
					)}
					{suggestionBadge('purpose')}
				</div>
				<div ref={purposeOptionsRef} className="flex flex-wrap gap-1.5">
					{purposeOptions.map((opt) => (
						<div key={opt} data-purpose-option={opt}>
							{withTooltip(
								<Button
									type="button"
									variant={label.purpose === opt ? 'default' : 'outline'}
									size="sm"
									className={cn(
										label.purpose === opt &&
											'ring-2 ring-primary ring-offset-2 ring-offset-background font-semibold'
									)}
									onClick={() => {
										if (label.purpose === opt) {
											onChange({ ...label, purpose: undefined, purposeCustom: undefined })
										} else {
											onChange({
												...label,
												purpose: opt,
												purposeCustom: opt === 'other_custom' ? label.purposeCustom : undefined,
											})
										}
									}}
									disabled={disabled}
									title={isSuggestedOption('purpose', opt) ? suggestionRationaleText : undefined}
								>
									{isSuggestedOption('purpose', opt) && (
										<Sparkles className="mr-1 size-3 shrink-0" aria-hidden />
									)}
									{purposeLabels[opt]}
								</Button>,
								purposeTooltips[opt],
								purposeTooltipSides[opt] ?? 'left',
								false
							)}
						</div>
					))}
				</div>
				{label.purpose === 'other_custom' && (
					<Input
						placeholder={t('pages.projectDetail.modal.labels.purposeCustomPlaceholder') as string}
						value={label.purposeCustom ?? ''}
						onChange={(e) => onChange({ ...label, purposeCustom: e.target.value || undefined })}
						className="mt-2 max-w-[500px]"
						maxLength={MAX_PURPOSE_CUSTOM_LENGTH}
						disabled={disabled}
					/>
				)}
			</div>
		</div>
	)
}
